import * as React from 'react';

import { Nullable } from '@common/typescript/objects/Nullable';

import {
	PetBreedSelectFilter,
} from '@app/store/SelectList/SelectsInterfaces';
import { CoreSelect } from '@app/components/UI/Inputs/CoreSelect';

interface BaseProps {
	petSpecieId: number | undefined;
	crematoryId?: number;

	disabled?: boolean;
	className?: string;
	selectClassName?: string;
	activeClassName?: string;

	pickDefault?: (entity) => number;
	onDeselect?: (id: number) => void;

	label?: string;
	placeholder?: string;
}

interface SingleProps extends BaseProps {
	value?: number;
	onChange: (value: number | undefined) => void;
	multiple?: false;
}

interface MultipleProps extends BaseProps {
	value?: Array<number>;
	onChange: (value: Array<number> | undefined) => void;
	multiple: true;
}

type OwnProps = SingleProps | MultipleProps;

function isEmptyValue(value?: number | Array<number>) {
	if (value === undefined) return true;
	if (Array.isArray(value)) return value.length === 0;

	return value === -1;
}

function shouldPickDefault(store, multiple, isEmptyValue, reqProps, value): boolean {
	return !store.isLoading
		&& store.items?.length && store.pagination.total === 1
		// eslint-disable-next-line sonarjs/no-all-duplicated-branches
		&& (multiple ? isEmptyValue?.(value) : isEmptyValue?.(value))
		&& reqProps.isEmpty(store.filters);
}

export const BreedSelect = (props: OwnProps): JSX.Element => {
	const [query, setQuery] = React.useState<string>('');

	const filters: Nullable<PetBreedSelectFilter> = React.useMemo(() => ({
		petSpecie: [props.petSpecieId || -1],
		crematoryId: props.crematoryId,
		name: query,
		search: query,
	}), [query, props.petSpecieId, props.crematoryId]);

	const properties = {
		...props,
		onSearch: setQuery,
		filters,
		shouldPickDefault,
		isEmptyValue,
		shouldFetch: props.petSpecieId !== -1,
	};

	// from PetBreedSelect
	const previousValue = React.useRef(props.petSpecieId);
	React.useEffect(() => {
		if (props.petSpecieId !== previousValue.current) {
			props.onChange(undefined);
			previousValue.current = props.petSpecieId;
		}
	}, [props.petSpecieId]);

	return <CoreSelect store="petBreeds" {...properties} />;
};
